import { useEffect, useRef, useState } from 'react';

interface ServicesProps {
  onGetStarted: () => void;
}

export default function Services({ onGetStarted }: ServicesProps) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const d = (ms: number) => `${ms}ms`;

  return (
    <section id="pricing" ref={sectionRef} className="py-12 sm:py-20 bg-white font-body">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div
          className={`text-center mb-10 sm:mb-14 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-[1px] bg-[#F5C518]"></div>
            <span className="text-[#F5C518] text-xs font-semibold tracking-[0.3em] uppercase">
              How It Works
            </span>
            <div className="w-8 h-[1px] bg-[#F5C518]"></div>
          </div>
          <h2 className="text-4xl sm:text-6xl md:text-7xl font-display tracking-wide text-[#1a1a1a]">
            pricing
          </h2>
          <p className="text-[#888] text-sm sm:text-base mt-4 max-w-xl mx-auto leading-relaxed">
            I build your website, then I&apos;ll keep it alive. Simple, transparent, no surprises.
          </p>
        </div>

        {/* Steps 1 & 2 — side by side */}
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-8">
          {/* STEP 1 — Build */}
          <div
            className={`relative bg-[#FAFAF8] rounded-lg border border-[#eee] overflow-hidden transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: d(200) }}
          >
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#F5C518]"></div>
            <div className="p-7 sm:p-9">
              {/* Step badge */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-full bg-[#F5C518] flex items-center justify-center">
                  <span className="text-[#1a1a1a] text-sm font-bold">1</span>
                </div>
                <span className="text-[#999] text-xs font-semibold tracking-[0.2em] uppercase">
                  First, I Build
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-display tracking-wide text-[#1a1a1a] mb-2">
                WEBSITE BUILD
              </h3>
              <div className="flex items-baseline gap-2 mb-5">
                <span className="text-4xl sm:text-5xl font-display tracking-wide text-[#1a1a1a]">
                  $180
                </span>
                <span className="text-4xl sm:text-5xl font-display tracking-wide text-[#1a1a1a]">
                  – $600
                </span>
              </div>
              <p className="text-[#777] text-sm leading-relaxed mb-6">
                A custom website designed and built to structure and organize your business for visitors. This won&apos;t cost you less than $180 or more than $600—pricing depends on the complexity and amount of content your site needs. We&apos;ll figure out where your site will fall in the range on a call.
              </p>

              <div className="mt-8">
                <button
                  onClick={() => window.open('https://form.jotform.com/260534658051052', '_blank')}
                  className="w-full bg-[#F5C518] text-[#1a1a1a] py-3.5 rounded-md text-[13px] font-semibold tracking-[0.12em] uppercase hover:bg-[#D4A90E] transition-all cursor-pointer whitespace-nowrap shadow-md shadow-[#F5C518]/15 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>

          {/* STEP 2 — Host & Maintain */}
          <div
            className={`relative bg-[#1a1a1a] rounded-lg overflow-hidden transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: d(350) }}
          >
            <div className="absolute top-0 left-0 right-0 h-[3px] bg-[#F5C518]"></div>
            <div className="p-7 sm:p-9">
              {/* Step badge */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-9 h-9 rounded-full bg-[#F5C518] flex items-center justify-center">
                  <span className="text-[#1a1a1a] text-sm font-bold">2</span>
                </div>
                <span className="text-white/40 text-xs font-semibold tracking-[0.2em] uppercase">
                  Then, I Keep It Live
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-display tracking-wide text-white mb-2">
                HOSTING & MAINTENANCE
              </h3>

              {/* Toggle */}
              <div className="flex items-center gap-1 bg-white/10 rounded-full p-1 w-fit mb-5">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                    billingCycle === 'monthly'
                      ? 'bg-[#F5C518] text-[#1a1a1a]'
                      : 'text-white/50 hover:text-white/70'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all cursor-pointer whitespace-nowrap ${
                    billingCycle === 'yearly'
                      ? 'bg-[#F5C518] text-[#1a1a1a]'
                      : 'text-white/50 hover:text-white/70'
                  }`}
                >
                  Yearly
                </button>
              </div>

              <div className="flex items-baseline gap-2 mb-5">
                {billingCycle === 'monthly' ? (
                  <>
                    <span className="text-4xl sm:text-5xl font-display tracking-wide text-white">
                      $59.99
                    </span>
                    <span className="text-white/40 text-sm">/month</span>
                  </>
                ) : (
                  <>
                    <span className="text-4xl sm:text-5xl font-display tracking-wide text-white">
                      $499
                    </span>
                    <span className="text-white/40 text-sm">/year</span>
                  </>
                )}
              </div>

              <p className="text-white/50 text-sm leading-relaxed mb-6">
                Your website stays online, secure, and supported. Hosting is required to keep your site live after the build.&nbsp;
              </p>

              <div className="w-full h-[1px] bg-white/10 mb-6"></div>

              <ul className="space-y-3 text-white/60 text-sm">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#F5C518] text-base mt-0.5 w-4 h-4 flex items-center justify-center"></i>
                  <span>
                    <strong className="text-white">Website hosting</strong> included
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#F5C518] text-base mt-0.5 w-4 h-4 flex items-center justify-center"></i>
                  <span>
                    <strong className="text-white">
                      {billingCycle === 'monthly' ? '1 update request per month' : '1 update request per year'}
                    </strong>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#F5C518] text-base mt-0.5 w-4 h-4 flex items-center justify-center"></i>
                  <span>Site stays fast, secure, and up to date</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-[#F5C518] text-base mt-0.5 w-4 h-4 flex items-center justify-center"></i>
                  <span>
                    <strong className="text-white">Cancel anytime</strong> — your site will be disconnected
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Connector line between steps and "as needed" */}
        <div
          className={`flex items-center justify-center my-2 transition-all duration-700 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: d(500) }}
        >
          <div className="w-[1px] h-8 bg-[#eee]"></div>
        </div>

        {/* AS NEEDED — Edits */}
        <div
          className={`relative max-w-4xl mx-auto bg-white rounded-lg border border-[#eee] overflow-hidden transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
          style={{ transitionDelay: d(550) }}
        >
          <div className="p-7 sm:p-9">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              {/* Left */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-full border-2 border-dashed border-[#F5C518] flex items-center justify-center">
                    <i className="ri-edit-line text-[#F5C518] text-sm w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <span className="text-[#999] text-xs font-semibold tracking-[0.2em] uppercase">
                    As Needed
                  </span>
                </div>
                <h3 className="text-xl sm:text-2xl font-display tracking-wide text-[#1a1a1a] mb-2">
                  ADDITIONAL EDITS
                </h3>
                <p className="text-[#777] text-sm leading-relaxed max-w-md">
                  Need more changes beyond your free request? Each additional request covers up to{' '}
                  <strong className="text-[#1a1a1a]">12 edits</strong> so you can batch your updates.
                </p>
              </div>

              {/* Right — Price */}
              <div className="md:text-right flex-shrink-0">
                <div className="flex items-baseline gap-2 md:justify-end">
                  <span className="text-4xl sm:text-5xl font-display tracking-wide text-[#1a1a1a]">
                    $32
                  </span>
                  <span className="text-[#999] text-sm">/request</span>
                </div>
                <p className="text-[#aaa] text-xs mt-1">Up to 12 edits per request</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div
          className={`text-center mt-8 sm:mt-12 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: d(700) }}
        >
        </div>
      </div>
    </section>
  );
}
